import { onError } from '@apollo/client/link/error'
import { Observable } from '@apollo/client'

import { refresh } from '../hooks/useSuspendedSession'
import { t } from '@lingui/core/macro'

/**
 * Apollo Error Link that handles authentication errors and retries.
 *
 * Authentication Flow:
 * 1. If a GraphQL operation returns an auth error (401 or specific error message)
 * 2. Attempt to refresh the authentication state silently
 * 3. If refresh succeeds (still signed in), retry the original operation once
 * 4. If retry also fails with auth error, show error alert to user
 * 5. If refresh fails, AuthProvider will handle the redirect to sign-in
 *
 * Note: Multiple simultaneous auth errors will share the same refresh promise,
 * preventing duplicate refresh attempts.
 */
export const apolloErrorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    // Check if this operation has already been retried
    const hasBeenRetried = operation.getContext().hasBeenRetried

    // Check for authentication errors in GraphQL errors
    const hasAuthError = graphQLErrors?.some(
      error =>
        error.message === 'unauthorized' ||
        error.extensions?.code === 'UNAUTHENTICATED'
    )

    // Also check for network-level 401s
    const isNetworkAuthError =
      networkError &&
      'statusCode' in networkError &&
      networkError.statusCode === 401

    if ((hasAuthError || isNetworkAuthError) && !hasBeenRetried) {
      return new Observable(observer => {
        refresh()
          .then(session => {
            if (session) {
              // Still authenticated, retry the operation once
              operation.setContext({
                hasBeenRetried: true
              })

              forward(operation)
            } else {
              // Not authenticated anymore
              observer.error(graphQLErrors || networkError)
            }
          })
          .catch(error => {
            console.error('[Auth] Error refreshing authentication:', error)

            observer.error(graphQLErrors || networkError)
          })
      })
    }

    // If this is a retry and we still get auth error, show error alert
    if ((hasAuthError || isNetworkAuthError) && hasBeenRetried) {
      console.error(
        '[Auth] Operation failed after refresh:',
        operation.operationName
      )

      alert(
        t({
          comment:
            'Error message that is shown to the user when he for any reason isn`t signed in anymore and needs to reload.',
          message:
            'Operation failed due to authentication error. Please try again or refresh the page.'
        })
      )
    }
  }
)
